import { AutoComplete, AutoCompleteProps, Input, SelectProps, Spin } from 'antd';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import classnames from 'classnames';
import styles from 'components/common/AutoCompleteInput/index.less';
import React, { useMemo } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { CusSelect } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleGroup/styled';
import { toLowerCase } from '@src/emp-3.0/utils';

interface IProps
  extends Omit<SelectProps<any, BaseOptionType | DefaultOptionType> & AutoCompleteProps, 'mode'> {
  mode?: 'autoComplete' | 'select';
  disabled?: boolean;
  active?: boolean;
  validateStatus?: 'error';
  value?: string | null;
  placeholder?: string;
  onChange: (s: string) => void;
  options?: any[];
  suffix?: React.ReactNode;
  status?: 'error' | 'warning';
  loading?: boolean;
  showArrow?: boolean;
  bordered?: boolean;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
  onSearch?: (text: string) => void;
}

export const AutoCompleteInput: React.FC<IProps> = (props: IProps) => {
  const {
    mode,
    placeholder,
    value,
    options,
    active,
    validateStatus,
    disabled = false,
    suffix,
    status,
    loading = false,
    showArrow,
    bordered,
    style,
    onChange,
    onFocus,
    onBlur,
    onSearch,
    ...rest
  } = props;

  const Suffix = useMemo(() => {
    if (loading) return <LoadingOutlined />;
    return suffix || <span />;
  }, [loading, suffix]);

  let renderedComponent;
  if (mode === 'select') {
    renderedComponent = (
      <CusSelect
        disabled={disabled}
        allowClear
        showSearch
        popupClassName={styles.customSelectDropdown}
        filterOption={(input, option) => toLowerCase(option?.value).includes(toLowerCase(input))}
        optionLabelProp="value" // Since the option has the mapped and blocked tag.
        style={style}
        active={active}
        placeholder="Please select..."
        value={value}
        options={options}
        onChange={(value) => {
          onChange(value);
        }}
        onFocus={(e) => {
          onFocus?.();
          e.stopPropagation();
        }}
        onBlur={onBlur}
        onClick={(e) => e.stopPropagation()}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        suffixIcon={Suffix}
        status={loading ? undefined : status}
        dropdownStyle={{ minWidth: '350px' }}
        dropdownAlign={{
          overflow: {
            adjustX: true,
            adjustY: false,
          },
        }}
        onSearch={onSearch}
        {...rest}
      />
    );
  } else {
    renderedComponent = (
      <div className={styles.autoCompleteInputWrapper} style={style}>
        <AutoComplete
          disabled={disabled}
          popupClassName={styles.customSelectDropdown}
          value={value}
          options={options}
          filterOption={(input, option) => toLowerCase(option?.value).includes(toLowerCase(input))}
          onChange={(value) => {
            onChange(value);
          }}
          onFocus={(e) => {
            onFocus?.();
            e.stopPropagation();
          }}
          onBlur={onBlur}
          onClick={(e) => e.stopPropagation()}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          dropdownRender={(menu) => {
            return (
              <Spin spinning={loading} indicator={<LoadingOutlined />}>
                {menu}
              </Spin>
            );
          }}
          showArrow={showArrow}
          dropdownMatchSelectWidth={350}
          dropdownAlign={{
            overflow: {
              adjustX: true,
              adjustY: false,
            },
          }}
          onSearch={onSearch}
          {...rest}
        >
          <Input
            placeholder={placeholder ? placeholder : 'Type or select...'}
            className={classnames({
              [styles.autoCompleteInputFocus]: active,
              [styles.autoCompleteInput]: !active,
              [styles.autoCompleteInputWithErrorBorder]: validateStatus === 'error',
              [styles.autoCompleteInputWithBorder]: !active && bordered && !validateStatus,
            })}
            status={loading ? undefined : status}
            suffix={Suffix}
          />
        </AutoComplete>
      </div>
    );
  }

  if (disabled && value) {
    return (
      <AddEllipsisWithTooltip
        text={value}
        tooltipText={value}
        dataIndex="name"
        style={{
          flex: 1,
          width: '150px',
          // To stay as consistent as possible with the antd component style.
          fontWeight: 500,
          padding: '4px 11px',
          letterSpacing: '-0.01em',
          border: 'solid 1px transparent',
        }}
      />
    );
  }
  return renderedComponent;
};
